import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import { normalizeId } from '@/utils/cacheKeys';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';
import {
  CachedEntry,
  IndexedAsyncStorageCache,
} from '@/services/_shared/IndexedAsyncStorageCache';
import { runPriorityConcurrencyQueue } from '@/services/_shared/PriorityConcurrencyQueue';

const logger = createScopedLogger('EPISODES-CACHE');

export interface CachedEpisodes {
  data: any[];
  timestamp: number;
  campaignId: string;
  seriesId: string;
  ttl: number;
}

export interface EpisodesCacheConfig {
  maxCachedSeries: number;
  ttl: number;
  prefetchConcurrency: number;
  prefetchDelay: number;
  wifiOnlyMode: boolean;
}

export interface PrefetchTask {
  seriesId: string;
  priority: 'high' | 'medium' | 'low';
  campaignId: string;
}

export interface EpisodesCacheStats {
  totalEntries: number;
  sizeKB: number;
  totalSeries: number;
  totalEpisodes: number;
  totalSizeKB: number;
  oldestEntry: Date | null;
  newestEntry: Date | null;
  cacheHitRate: number;
}

export type CacheStats = EpisodesCacheStats;

const EPISODES_CACHE_PREFIX = '@episodes_cache_';
const EPISODES_INDEX_KEY = '@episodes_cache_index';
const CACHE_STATS_KEY = '@episodes_cache_stats';

const DEFAULT_CONFIG: EpisodesCacheConfig = {
  maxCachedSeries: 2000,
  ttl: 24 * 60 * 60 * 1000,
  prefetchConcurrency: 3,
  prefetchDelay: 2000,
  wifiOnlyMode: true,
};

class EpisodesCacheService extends IndexedAsyncStorageCache<CachedEpisodes> {
  private config: EpisodesCacheConfig = DEFAULT_CONFIG;
  private abortControllers: Map<string, AbortController> = new Map();
  private prioritySeriesIds: Set<string> = new Set();
  private stats = {
    cacheHits: 0,
    cacheMisses: 0,
    apiCalls: 0,
  };

  constructor() {
    super({
      indexKey: EPISODES_INDEX_KEY,
      logTag: '[EPISODES-CACHE]',
      defaultTtlMs: DEFAULT_CONFIG.ttl,
      quotaEvictionBatch: 20,
      maxQuotaRetries: 3,
      cleanupOnInit: true,
    });
  }

  async initialize(): Promise<void> {
    await super.initialize();
    try {
      const statsData = await AsyncStorage.getItem(CACHE_STATS_KEY);
      if (statsData) {
        this.stats = JSON.parse(statsData);
      }
    } catch (error) {
      logger.error('Error loading cache stats:', error);
    }
  }

  setPrioritySeriesIds(seriesIds: string[]): void {
    this.prioritySeriesIds = new Set(seriesIds);
  }

  private extractSeriesIdFromCacheKey(cacheKey: string): string | null {
    const parts = cacheKey.replace(EPISODES_CACHE_PREFIX, '').split('_');
    return parts.length >= 2 ? parts[parts.length - 1] : null;
  }

  protected async evictOldestEntries(count: number): Promise<number> {
    const entries: Array<{ key: string; timestamp: number; isPriority: boolean }> = [];

    for (const cacheKey of this.cacheIndex) {
      try {
        const cached = await AsyncStorage.getItem(cacheKey);
        if (cached) {
          const parsed: CachedEntry<CachedEpisodes> = JSON.parse(cached);
          const timestamp =
            (parsed.data && typeof parsed.data === 'object' && 'timestamp' in parsed.data
              ? (parsed.data as CachedEpisodes).timestamp
              : parsed.timestamp) ?? 0;
          const seriesId = this.extractSeriesIdFromCacheKey(cacheKey);
          entries.push({
            key: cacheKey,
            timestamp,
            isPriority: seriesId ? this.prioritySeriesIds.has(seriesId) : false,
          });
        }
      } catch {
        entries.push({ key: cacheKey, timestamp: 0, isPriority: false });
      }
    }

    const nonPriorityEntries = entries
      .filter((e) => !e.isPriority)
      .sort((a, b) => a.timestamp - b.timestamp);

    const allEntriesSorted = entries.sort((a, b) => a.timestamp - b.timestamp);
    const evictionCandidates =
      nonPriorityEntries.length > 0
        ? nonPriorityEntries.slice(0, count)
        : allEntriesSorted.slice(0, count);

    if (evictionCandidates.length === 0) return 0;

    const keysToRemove = evictionCandidates.map((e) => e.key);
    await AsyncStorage.multiRemove(keysToRemove);
    keysToRemove.forEach((key) => this.cacheIndex.delete(key));
    await this.persistIndex();

    return keysToRemove.length;
  }

  private generateCacheKey(campaignId: string, seriesId: string | number): string {
    return `${EPISODES_CACHE_PREFIX}${campaignId}_${normalizeId(seriesId)}`;
  }

  async getEpisodes(campaignId: string, seriesId: string): Promise<any[] | null> {
    await this.initialize();
    const cacheKey = this.generateCacheKey(campaignId, seriesId);

    try {
      const cached = await AsyncStorage.getItem(cacheKey);
      if (!cached) {
        this.stats.cacheMisses++;
        await this.persistStats();
        return null;
      }

      const cachedEpisodes: CachedEpisodes = JSON.parse(cached);
      const age = Date.now() - cachedEpisodes.timestamp;

      if (age > cachedEpisodes.ttl) {
        await this.remove(campaignId, seriesId);
        this.stats.cacheMisses++;
        await this.persistStats();
        return null;
      }

      this.stats.cacheHits++;
      await this.persistStats();
      return cachedEpisodes.data;
    } catch (error) {
      logger.error(`Error reading cache for series ${seriesId}:`, error);
      this.stats.cacheMisses++;
      await this.persistStats();
      return null;
    }
  }

  async setEpisodes(campaignId: string, seriesId: string, episodes: any[]): Promise<void> {
    await this.initialize();
    const cacheKey = this.generateCacheKey(campaignId, seriesId);

    try {
      const isPriority = this.prioritySeriesIds.has(normalizeId(seriesId).toString());
      if (this.quotaExhausted && !isPriority) {
        return;
      }

      const cachedEpisodes: CachedEpisodes = {
        data: episodes,
        timestamp: Date.now(),
        campaignId,
        seriesId,
        ttl: this.config.ttl,
      };

      const stored = await this.setItemWithQuotaHandling(cacheKey, JSON.stringify(cachedEpisodes));
      if (!stored) return;

      this.cacheIndex.add(cacheKey);

      if (this.cacheIndex.size > this.config.maxCachedSeries) {
        await this.enforceMaxEntries();
      }

      await this.persistIndex();

      try {
        const { episodeDetailsCacheService } = await import('@/services/episodeDetailsCacheService');
        const episodeDetailsToCache = episodes
          .filter((ep) => ep.episode_id && ep.contents_series)
          .map((ep) => ({
            episode_id: ep.episode_id,
            title: ep.title,
            description: ep.description,
            episode_position: ep.episode_position,
            season_position: ep.season_position,
            duration: ep.duration,
            series_id: ep.series_id,
            url_streaming_no_drm: ep.url_streaming_no_drm,
            url_streaming_drm: ep.url_streaming_drm,
            is_free: ep.is_free,
            contents_series: ep.contents_series,
          }));

        if (episodeDetailsToCache.length > 0) {
          await episodeDetailsCacheService.setBulkEpisodeDetails(campaignId, episodeDetailsToCache);
        }
      } catch (detailsCacheError) {
        logger.error('Error caching episode details:', detailsCacheError);
      }
    } catch (error) {
      logger.error(`Error storing cache for series ${seriesId}:`, error);
    }
  }

  async cacheEpisodesBatch(
    campaignId: string,
    episodesMap: Map<string, any[]>
  ): Promise<{ successful: number; failed: number; totalEpisodes: number }> {
    await this.initialize();

    let successful = 0;
    let failed = 0;
    let totalEpisodes = 0;

    try {
      const cacheOperations = Array.from(episodesMap.entries()).map(([seriesId, episodes]) => {
        totalEpisodes += episodes.length;
        return this.setEpisodes(campaignId, seriesId, episodes)
          .then(() => {
            successful++;
            return { seriesId, success: true };
          })
          .catch((error) => {
            failed++;
            logger.error(`Failed to cache series ${seriesId}:`, error);
            return { seriesId, success: false, error };
          });
      });

      await Promise.allSettled(cacheOperations);
      return { successful, failed, totalEpisodes };
    } catch (error) {
      logger.error('Unexpected batch cache error:', error);
      return { successful, failed, totalEpisodes };
    }
  }

  async hasEpisodes(campaignId: string, seriesId: string): Promise<boolean> {
    await this.initialize();
    const cacheKey = this.generateCacheKey(campaignId, seriesId);

    try {
      const cached = await AsyncStorage.getItem(cacheKey);
      if (!cached) return false;
      const cachedEpisodes: CachedEpisodes = JSON.parse(cached);
      const age = Date.now() - cachedEpisodes.timestamp;
      return age <= cachedEpisodes.ttl;
    } catch {
      return false;
    }
  }

  async remove(campaignId: string, seriesId: string): Promise<void> {
    await this.initialize();
    const cacheKey = this.generateCacheKey(campaignId, seriesId);
    try {
      await AsyncStorage.removeItem(cacheKey);
      this.cacheIndex.delete(cacheKey);
      await this.persistIndex();
    } catch (error) {
      logger.error(`Error removing cache for series ${seriesId}:`, error);
    }
  }

  async cleanupExpired(): Promise<void> {
    const keys = [...this.cacheIndex];
    let removedCount = 0;
    try {
      for (const cacheKey of keys) {
        const cached = await AsyncStorage.getItem(cacheKey);
        if (!cached) {
          this.cacheIndex.delete(cacheKey);
          removedCount++;
          continue;
        }
        try {
          const cachedEpisodes: CachedEpisodes = JSON.parse(cached);
          const age = Date.now() - cachedEpisodes.timestamp;
          if (age > cachedEpisodes.ttl) {
            await AsyncStorage.removeItem(cacheKey);
            this.cacheIndex.delete(cacheKey);
            removedCount++;
          }
        } catch {
          await AsyncStorage.removeItem(cacheKey);
          this.cacheIndex.delete(cacheKey);
          removedCount++;
        }
      }
      if (removedCount > 0) await this.persistIndex();
    } catch (error) {
      logger.error('Error during cleanup:', error);
    }
  }

  async clearCampaign(campaignId: string): Promise<void> {
    await this.initialize();
    try {
      const keysToRemove = [...this.cacheIndex].filter((key) =>
        key.includes(`${EPISODES_CACHE_PREFIX}${campaignId}_`)
      );
      if (keysToRemove.length > 0) {
        await AsyncStorage.multiRemove(keysToRemove);
        keysToRemove.forEach((key) => this.cacheIndex.delete(key));
        await this.persistIndex();
      }
    } catch (error) {
      logger.error('Error clearing campaign cache:', error);
    }
  }

  async clear(): Promise<void> {
    await super.clear();
    try {
      await AsyncStorage.removeItem(CACHE_STATS_KEY);
      this.stats = { cacheHits: 0, cacheMisses: 0, apiCalls: 0 };
    } catch (error) {
      logger.error('Error clearing stats:', error);
    }
  }

  private async enforceMaxEntries(): Promise<void> {
    const entriesToRemove = this.cacheIndex.size - this.config.maxCachedSeries;
    if (entriesToRemove <= 0) return;

    const entries: Array<{ key: string; timestamp: number }> = [];
    for (const cacheKey of this.cacheIndex) {
      try {
        const cached = await AsyncStorage.getItem(cacheKey);
        if (cached) {
          const cachedEpisodes: CachedEpisodes = JSON.parse(cached);
          entries.push({ key: cacheKey, timestamp: cachedEpisodes.timestamp });
        }
      } catch {
        entries.push({ key: cacheKey, timestamp: 0 });
      }
    }

    entries.sort((a, b) => a.timestamp - b.timestamp);
    const keysToRemove = entries.slice(0, entriesToRemove).map((e) => e.key);
    await AsyncStorage.multiRemove(keysToRemove);
    keysToRemove.forEach((key) => this.cacheIndex.delete(key));
    await this.persistIndex();
  }

  async getCacheStats(): Promise<EpisodesCacheStats> {
    await this.initialize();

    const keys = [...this.cacheIndex];
    let oldestTimestamp = Number.MAX_SAFE_INTEGER;
    let newestTimestamp = 0;
    let totalSize = 0;
    let totalEpisodes = 0;

    for (const cacheKey of keys) {
      try {
        const cached = await AsyncStorage.getItem(cacheKey);
        if (cached) {
          totalSize += cached.length * 2;
          const cachedEpisodes: CachedEpisodes = JSON.parse(cached);
          totalEpisodes += cachedEpisodes.data.length;
          oldestTimestamp = Math.min(oldestTimestamp, cachedEpisodes.timestamp);
          newestTimestamp = Math.max(newestTimestamp, cachedEpisodes.timestamp);
        }
      } catch {
        // Skip invalid entries
      }
    }

    const totalRequests = this.stats.cacheHits + this.stats.cacheMisses;
    const hitRate = totalRequests > 0 ? (this.stats.cacheHits / totalRequests) * 100 : 0;

    const sizeKB = Math.round(totalSize / 1024);
    return {
      totalEntries: keys.length,
      sizeKB,
      totalSeries: keys.length,
      totalEpisodes,
      totalSizeKB: sizeKB,
      oldestEntry: oldestTimestamp !== Number.MAX_SAFE_INTEGER ? new Date(oldestTimestamp) : null,
      newestEntry: newestTimestamp !== 0 ? new Date(newestTimestamp) : null,
      cacheHitRate: Math.round(hitRate),
    };
  }

  async prefetchSeriesEpisodes(
    tasks: PrefetchTask[],
    fetchFunction: (campaignId: string, seriesId: string) => Promise<any[]>
  ): Promise<{ total: number; cached: number; fetched: number }> {
    await this.initialize();

    if (!(await this.shouldPrefetch())) {
      return { total: tasks.length, cached: tasks.length, fetched: 0 };
    }

    const validTasks = await this.filterValidTasks(tasks);
    if (validTasks.length === 0) {
      return { total: tasks.length, cached: tasks.length, fetched: 0 };
    }

    const outcome = await runPriorityConcurrencyQueue<PrefetchTask, boolean>({
      tasks: validTasks,
      concurrency: this.config.prefetchConcurrency,
      worker: (task) => this.prefetchTask(task, fetchFunction),
      pollIntervalMs: 100,
    });

    const successfulTasks = outcome.results.filter((r) => r.result === true).length;
    return { total: outcome.total, cached: 0, fetched: successfulTasks };
  }

  private async shouldPrefetch(): Promise<boolean> {
    try {
      const netInfo = await NetInfo.fetch();
      if (this.config.wifiOnlyMode && netInfo.type !== 'wifi') return false;
      if (!netInfo.isConnected) return false;
      return true;
    } catch (error) {
      logger.error('Error checking prefetch conditions:', error);
      return true;
    }
  }

  private async filterValidTasks(tasks: PrefetchTask[]): Promise<PrefetchTask[]> {
    const validTasks: PrefetchTask[] = [];
    const seen = new Set<string>();

    for (const task of tasks) {
      const key = `${task.campaignId}_${task.seriesId}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const hasCache = await this.hasEpisodes(task.campaignId, task.seriesId);
      if (!hasCache) validTasks.push(task);
    }

    return validTasks;
  }

  private async prefetchTask(
    task: PrefetchTask,
    fetchFunction: (campaignId: string, seriesId: string) => Promise<any[]>
  ): Promise<boolean> {
    const abortController = new AbortController();
    this.abortControllers.set(task.seriesId, abortController);

    const timeoutId = setTimeout(() => abortController.abort(), 10000);
    let success = false;

    try {
      const episodes = await fetchFunction(task.campaignId, task.seriesId);
      if (!abortController.signal.aborted && episodes && episodes.length > 0) {
        await this.setEpisodes(task.campaignId, task.seriesId, episodes);
        this.stats.apiCalls++;
        await this.persistStats();
        success = true;
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        logger.error(`Error prefetching series ${task.seriesId}:`, error);
      }
    } finally {
      clearTimeout(timeoutId);
      this.abortControllers.delete(task.seriesId);
    }

    return success;
  }

  cancelPrefetch(seriesId: string): void {
    const controller = this.abortControllers.get(seriesId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(seriesId);
    }
  }

  cancelAllPrefetches(): void {
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
  }

  private async persistStats(): Promise<void> {
    try {
      await AsyncStorage.setItem(CACHE_STATS_KEY, JSON.stringify(this.stats));
    } catch (error) {
      logger.error('Error persisting stats:', error);
    }
  }

  updateConfig(config: Partial<EpisodesCacheConfig>): void {
    this.config = { ...this.config, ...config };
  }

  getConfig(): EpisodesCacheConfig {
    return { ...this.config };
  }

  async prefetchAllSeriesForCampaign(
    allSeries: { serie_id: number }[],
    campaignId: string,
    fetchFunction: (campaignId: string, seriesId: string) => Promise<any[]>
  ): Promise<{ total: number; fetched: number; cached: number; failed: number }> {
    await this.initialize();

    if (!(await this.shouldPrefetch())) {
      return { total: allSeries.length, fetched: 0, cached: allSeries.length, failed: 0 };
    }

    let cacheHits = 0;
    let successful = 0;
    let failed = 0;
    const seriesToFetch: { serie_id: number }[] = [];

    for (const series of allSeries) {
      const seriesId = series.serie_id.toString();
      const hasCache = await this.hasEpisodes(campaignId, seriesId);
      if (hasCache) {
        cacheHits++;
      } else {
        seriesToFetch.push(series);
      }
    }

    if (seriesToFetch.length === 0) {
      return { total: allSeries.length, fetched: 0, cached: cacheHits, failed: 0 };
    }

    for (const series of seriesToFetch) {
      const seriesId = series.serie_id.toString();
      try {
        const episodes = await fetchFunction(campaignId, seriesId);
        if (episodes && episodes.length > 0) {
          await this.setEpisodes(campaignId, seriesId, episodes);
          successful++;
        } else {
          failed++;
        }
      } catch (error) {
        logger.error(`Error fetching series ${seriesId}:`, error);
        failed++;
      }
    }

    return { total: allSeries.length, fetched: successful, cached: cacheHits, failed };
  }

  async getCachedSeriesIds(campaignId: string): Promise<string[]> {
    await this.initialize();
    const seriesIds: string[] = [];
    for (const cacheKey of this.cacheIndex) {
      if (cacheKey.includes(`${EPISODES_CACHE_PREFIX}${campaignId}_`)) {
        const seriesId = cacheKey.split(`${EPISODES_CACHE_PREFIX}${campaignId}_`)[1];
        if (seriesId) seriesIds.push(seriesId);
      }
    }
    return seriesIds;
  }
}

export const episodesCacheService = new EpisodesCacheService();
